import { useCallback, useEffect } from 'react';

import { BookIcon } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import ContextualHelp from '@/components/atoms/ContextualHelp/ContextualHelp';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Switch } from '@/components/ui/switch';
import useBrains from '@/hooks/useBrains';
import { languageSupportsEnhance } from '@/redux/dialogs/helper';
import { selectSelectedNode } from '@/redux/dialogs/selectors';
import { selectFeatureFlags } from '@/redux/featureFlag/selectors';
import { updateNode } from '@/redux/nodes/actions';
import { selectAccountId, selectBrainId } from '@/redux/session/selectors';
import { RootState } from '@/shared/types/state';
import { EVENT_UNKNOWN_DOCS_URL } from '@/util/constants';

import ToolkitWrapper from '../ToolkitWrapper';
import { AssignedReminders } from './AssignedReminders';

type FormType = { enhance: boolean };

const ToolkitNodeUnknown = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const brainId = useSelector(selectBrainId);
  const { brain } = useBrains(brainId);
  const accountId = useSelector(selectAccountId);
  const features = useSelector(selectFeatureFlags);

  const isEnhanceAvailable = languageSupportsEnhance(brain?.language, accountId, features);
  const { nodeId, enhance, isRootNodeUnknown } = useSelector((state: RootState) => {
    const selectedNode = selectSelectedNode(state);
    const isRootNodeUnknown = state.nodes?.nodes[0]?.type === 'unknown';

    return {
      nodeId: selectedNode.node_id,
      enhance: selectedNode?.enhance ?? false,
      isRootNodeUnknown,
    };
  }, shallowEqual);

  const { getValues, control, reset } = useForm<FormType>({ mode: 'onChange' });

  useEffect(() => {
    if (isEnhanceAvailable) {
      reset({ enhance: enhance });
    }
  }, [isEnhanceAvailable, enhance, reset]);

  const onChange = useCallback(async () => {
    const values = getValues();

    dispatch(updateNode({ nodeId, node: { enhance: values?.enhance ?? false } }));
  }, [dispatch, getValues, nodeId]);

  return (
    <ToolkitWrapper type={isRootNodeUnknown ? 'unknown_root' : 'unknown'}>
      <ContextualHelp
        preventClose
        name="action-close"
        title={t('dialog.unknown.title')}
        links={[
          { label: t('docs.docs'), url: EVENT_UNKNOWN_DOCS_URL, icon: <BookIcon size={16} /> },
        ]}
      >
        {t('dialog.unknown.body')}
      </ContextualHelp>
      {isEnhanceAvailable && (
        <Controller
          name="enhance"
          control={control}
          render={({ field: { value, onChange: onControllerChange } }) => {
            return (
              <InfoTooltip asChild={false} content={t('dialog.unknown.enhance_with_ai_tooltip')}>
                <Switch
                  label={t('dialog.unknown.enhance_with_ai')}
                  checked={value}
                  onCheckedChange={(event) => {
                    onControllerChange(event);
                    onChange();
                  }}
                />
              </InfoTooltip>
            );
          }}
        />
      )}

      <div className="my-6">
        <AssignedReminders />
      </div>
    </ToolkitWrapper>
  );
};

export default ToolkitNodeUnknown;
